import { FocusEventHandler, forwardRef, ReactNode, useId } from 'react';
import { Key, Select } from '@backstage/ui';

import { FieldHeader, FieldMessage, joinIds } from '../Field/Field';
import styles from './Selectfield.module.css';

export type SelectfieldOption = {
  label: string;
  value: string;
};

/** Options grouped under a section title, keyed by the title. */
export type SelectfieldGroupedOptions = Record<string, SelectfieldOption[]>;

/**
 * Argument of `onChange`. It has the shape of the MUI select change event
 * (`event.target.value`), so handlers written for MUI and `react-hook-form`
 * controllers keep working.
 */
export type SelectfieldChangeEvent = {
  target: {
    name: string;
    /** The selected value, or the selected values when `multiple` is set. */
    value: string | string[];
  };
};

export interface SelectfieldProps {
  name: string;
  id?: string;
  label?: string;
  /** Error message below the field; a non-empty value marks it invalid. */
  helperText?: ReactNode;
  /** Neutral help text below the field, replaced by `helperText`. */
  description?: ReactNode;
  /** Marks the field invalid without a message. */
  error?: boolean;
  /** Options when `isGroupSelect` is not set. */
  selectOption?: SelectfieldOption[];
  placeholder?: string;
  /** Text of the info button next to the label. */
  tooltip?: ReactNode;
  /** Element at the right end of the label row, for example a spinner. */
  actionItem?: ReactNode;
  /** Allows selecting several options; the value is then a string array. */
  multiple?: boolean;
  /** Selected values of a `multiple` select; takes precedence over `value`. */
  multiSelectedValue?: string[];
  /** Uses `groupSelectOptions` and shows a section per group. */
  isGroupSelect?: boolean;
  groupSelectOptions?: SelectfieldGroupedOptions;
  value?: string | string[] | null;
  /**
   * Called with the new value in `event.target.value`. Declared as a method
   * so handlers typed for MUI or DOM change events stay assignable.
   */
  onChange?(event: SelectfieldChangeEvent): void;
  onBlur?: FocusEventHandler<Element>;
  disabled?: boolean;
  required?: boolean;
  /** Shows a red asterisk after the label. */
  requiredMark?: boolean;
  /** Hides the chevron of the trigger. */
  showDropdownIcon?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  /** @deprecated The trigger always shows the label of the selected option. */
  showSelectedLabel?: boolean;
  'aria-label'?: string;
}

const toOptions = (
  isGroupSelect: boolean,
  selectOption: SelectfieldOption[],
  groupSelectOptions: SelectfieldGroupedOptions,
) => {
  const toOption = (option: SelectfieldOption) => ({
    id: option.value,
    label: option.label,
  });

  if (isGroupSelect) {
    return Object.entries(groupSelectOptions).map(([title, options]) => ({
      title,
      options: options.map(toOption),
    }));
  }
  return selectOption.map(toOption);
};

const toValues = (selection: Key | Key[] | null): string | string[] => {
  if (Array.isArray(selection)) {
    return selection.map(String);
  }
  return selection === null ? '' : String(selection);
};

/**
 * Dropdown of the Xelerate forms on the Backstage UI `Select`: single or
 * multiple selection, optional sections, the shared label row and message.
 */
export const Selectfield = forwardRef<HTMLDivElement, SelectfieldProps>(
  (props, ref) => {
    const {
      name,
      id,
      label,
      helperText,
      description,
      error = false,
      selectOption = [],
      placeholder,
      tooltip,
      actionItem = null,
      multiple = false,
      multiSelectedValue,
      isGroupSelect = false,
      groupSelectOptions = {},
      value,
      onChange,
      onBlur,
      disabled,
      required,
      requiredMark = false,
      showDropdownIcon = true,
      size = 'medium',
      className,
      'aria-label': ariaLabel,
    } = props;

    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const labelId = `${fieldId}-label`;
    const messageId = `${fieldId}-message`;
    const isInvalid = error || !!helperText;
    const message = helperText || description;

    const options = toOptions(isGroupSelect, selectOption, groupSelectOptions);

    const handleChange = (selection: Key | Key[] | null) => {
      onChange?.({ target: { name, value: toValues(selection) } });
    };

    const labelProps = label
      ? { 'aria-labelledby': labelId }
      : { 'aria-label': ariaLabel ?? placeholder ?? name };

    const sharedProps = {
      ...labelProps,
      id: fieldId,
      name,
      options,
      placeholder: placeholder || undefined,
      isDisabled: disabled,
      isRequired: required,
      size,
      onBlur,
      'aria-describedby': joinIds(!!message && messageId),
    };

    return (
      <div
        className={[styles.root, 'xlrt-Selectfield', className]
          .filter(Boolean)
          .join(' ')}
        data-invalid={isInvalid || undefined}
        data-hide-chevron={!showDropdownIcon || undefined}
      >
        <FieldHeader
          label={label}
          labelId={labelId}
          tooltip={tooltip}
          requiredMark={requiredMark}
          actionItem={actionItem}
        />
        {multiple ? (
          <Select
            {...sharedProps}
            ref={ref}
            selectionMode="multiple"
            value={multiSelectedValue ?? (Array.isArray(value) ? value : [])}
            onChange={handleChange}
          />
        ) : (
          <Select
            {...sharedProps}
            ref={ref}
            selectionMode="single"
            value={typeof value === 'string' && value !== '' ? value : null}
            onChange={handleChange}
          />
        )}
        <FieldMessage id={messageId} invalid={!!helperText}>
          {message}
        </FieldMessage>
      </div>
    );
  },
);

Selectfield.displayName = 'Selectfield';
